/**
 * @fileoverview Сценарные старты смены (не путать с длительностью ModeId).
 */

import { APRON } from "./world";
import { createPlane, spawnPlane } from "../plane/spawn";
import { spawnMedevac } from "../systems/events";
import type { GameHooks, GameState } from "../state/types";

/** Id сценария старта (поверх режима длительности). */
export type ScenarioId = "free" | "blizzard" | "medevac" | "gate_queue";

export interface ScenarioDef {
  id: ScenarioId;
  label: string;
  sub: string;
}

/** Карточки сценариев в меню. */
export const SCENARIOS: readonly ScenarioDef[] = [
  {
    id: "free",
    label: "Свободная смена",
    sub: "Обычный старт · учитесь в темпе режима",
  },
  {
    id: "blizzard",
    label: "Метель",
    sub: "Грязные полосы · сильный снег · плохая видимость",
  },
  {
    id: "medevac",
    label: "Медрейс",
    sub: "Резерв 09L · приоритетный заход · отвести остальных",
  },
  {
    id: "gate_queue",
    label: "Очередь на гейты",
    sub: "Все гейты заняты · два борта ждут на перроне",
  },
];

const SCENARIO_KEY = "asf_scenario_v1";

/** Прочитать сохраненный сценарий. */
export function loadScenario(): ScenarioId {
  try {
    const m = localStorage.getItem(SCENARIO_KEY);
    if (m && SCENARIOS.some((s) => s.id === m)) return m as ScenarioId;
  } catch {
    /* игнор */
  }
  return "free";
}

/** Сохранить сценарий. */
export function saveScenario(id: ScenarioId): void {
  try {
    localStorage.setItem(SCENARIO_KEY, id);
  } catch {
    /* игнор */
  }
}

function seedAtGate(
  g: GameState,
  gi: number,
  typeKey: "jet" | "regional" | "cargo",
  svcLeft: number,
): void {
  const gate = g.gates[gi];
  if (!gate) return;
  const p = createPlane(g, typeKey, false);
  p.status = "service";
  p.gate = gi;
  p.alt = 0;
  p.v = 0;
  p.ang = -Math.PI / 2;
  p.x = gate.x;
  p.y = gate.y;
  p.svcTotal = Math.max(svcLeft, p.T.svc);
  p.svcLeft = svcLeft;
  p.pat = 1 - svcLeft / Math.max(0.1, p.svcTotal);
  p.fade = 1;
  gate.plane = p;
  g.planes.push(p);
}

function seedAwaitingGate(g: GameState, apronIdx: number): void {
  const spot = APRON[apronIdx % APRON.length]!;
  const p = createPlane(g, apronIdx % 2 ? "regional" : "jet", false);
  p.status = "awaiting_gate";
  p.apron = { x: spot.x, y: spot.y };
  p.x = spot.x;
  p.y = spot.y;
  p.alt = 0;
  p.v = 0;
  p.ang = -Math.PI / 2;
  p.path = [];
  p.gateAskT = g.elapsed;
  p.waited = 6 + apronIdx * 4;
  p.pat = Math.max(0, Math.min(1, 1 - p.waited / 38));
  p.fade = 1;
  g.planes.push(p);
}

/**
 * Применить сценарий после createGame (не для туториала).
 * Мутирует начальное состояние: снег, борта, резерв.
 */
export function applyScenario(
  g: GameState,
  hooks: GameHooks,
  id: ScenarioId,
): void {
  g.scenarioId = id;
  if (id === "free") {
    g.scenarioLabel = "";
    return;
  }

  if (id === "blizzard") {
    g.scenarioLabel = "Метель";
    g.wxName = "Сценарий: метель";
    g.snowBoost = 0.95;
    g.snowBoostT = 45;
    g.snowRate = 1.45;
    g.vis = 0.3;
    g.visTarget = 0.38;
    g.visT = 35;
    const a = g.runways[0]!;
    const b = g.runways[1]!;
    a.snow = 82;
    b.snow = 68;
    a.ice = 28;
    b.ice = 14;
    a.critArmed = true;
    b.critArmed = true;
    spawnPlane(g, hooks, "jet", false);
    spawnPlane(g, hooks, "regional", false);
    hooks.toast(
      "СЦЕНАРИЙ «МЕТЕЛЬ»: полосы грязные — убирайте снег и не копите риск",
      "wx",
    );
    return;
  }

  if (id === "medevac") {
    g.scenarioLabel = "Медрейс";
    g.wxName = "Сценарий: медрейс";
    spawnPlane(g, hooks, "jet", false);
    spawnPlane(g, hooks, "cargo", false);
    const med = spawnMedevac(g, hooks, "Медрейс");
    const r = g.runways[0]!;
    r.reservedFor = med.id;
    r.reserveT = 28;
    r.reserveLabel = med.cs;
    r.snow = Math.max(r.snow, 35);
    hooks.toast(
      `СЦЕНАРИЙ «МЕДРЕЙС»: ${r.name} под ${med.cs} · посадите медрейс или откройте запасную`,
      "wx",
    );
    hooks.selectPlane?.(med);
    return;
  }

  if (id === "gate_queue") {
    g.scenarioLabel = "Очередь на гейты";
    g.wxName = "Сценарий: очередь на гейты";
    seedAtGate(g, 0, "jet", 11);
    seedAtGate(g, 1, "regional", 7);
    seedAtGate(g, 2, "cargo", 16);
    seedAtGate(g, 3, "jet", 9);
    seedAwaitingGate(g, 0);
    seedAwaitingGate(g, 2);
    spawnPlane(g, hooks, "regional", false);
    g.spawnT = 10;
    hooks.toast(
      "СЦЕНАРИЙ «ОЧЕРЕДЬ»: освобождайте гейты быстрее — два борта уже ждут на перроне",
      "wx",
    );
    const waiter = g.planes.find((p) => p.status === "awaiting_gate");
    if (waiter) hooks.selectPlane?.(waiter);
  }
}
